import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Grid2,
  CircularProgress,
} from "@mui/material";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { format, formatDistanceToNow } from "date-fns";
import { useGetDeviceConnectionDetailsQuery } from "../../redux/slices/djangoApi";

const formatBytes = (bytes) => {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const InfoRow = ({ label, value }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    sx={{ py: 0.5 }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value || "—"}
    </Typography>
  </Box>
);

const ConnectionStatusCard = ({ device }) => {
  const { data: emqx, isLoading: emqxLoading } =
    useGetDeviceConnectionDetailsQuery(device?.serial_number, {
      skip: !device?.serial_number,
    });

  const isOnline = device?.status === "ONLINE";
  const connectedAt = emqx?.connected_at;

  const sessionUptime = connectedAt
    ? formatDistanceToNow(new Date(connectedAt), { addSuffix: false })
    : null;

  const lastContactDate = device?.last_contact_time || emqx?.connected_at;
  const lastContact = lastContactDate
    ? format(new Date(lastContactDate), "MMM dd, yyyy h:mm a")
    : null;

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          {isOnline ? (
            <WifiIcon color="success" />
          ) : (
            <WifiOffIcon color="error" />
          )}
          <Typography variant="subtitle1" fontWeight="bold">
            Connection
          </Typography>
          <Box sx={{ ml: "auto" }}>
            <Chip
              label={device?.status || "OFFLINE"}
              color={isOnline ? "success" : "error"}
              size="small"
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {sessionUptime && (
          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 1.5 }}>
            <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary">
              Session uptime:
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {sessionUptime}
            </Typography>
          </Box>
        )}

        <InfoRow label="IP Address" value={emqx?.ip_address} />
        <InfoRow label="Last Contact" value={lastContact} />

        {emqxLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={20} />
          </Box>
        ) : emqx && Object.keys(emqx).length > 0 ? (
          <>
            <InfoRow
              label="Keepalive"
              value={emqx?.keepalive != null ? `${emqx.keepalive}s` : null}
            />
            <InfoRow
              label="MQTT Version"
              value={
                emqx?.proto_ver === 5
                  ? "5.0"
                  : emqx?.proto_ver === 4
                  ? "3.1.1"
                  : emqx?.proto_ver === 3
                  ? "3.1"
                  : emqx?.proto_ver != null
                  ? String(emqx.proto_ver)
                  : null
              }
            />
            <InfoRow
              label="Subscriptions"
              value={
                emqx?.subscriptions_cnt != null
                  ? String(emqx.subscriptions_cnt)
                  : null
              }
            />

            <Divider sx={{ my: 1.5 }} />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Traffic
            </Typography>
            <Grid2 container spacing={1}>
              <Grid2 size={6}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Messages In
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {emqx?.recv_msg != null ? emqx.recv_msg : "—"}
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 size={6}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Messages Out
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {emqx?.send_msg != null ? emqx.send_msg : "—"}
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 size={6}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Data Received
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatBytes(emqx?.recv_oct)}
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 size={6}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Data Sent
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatBytes(emqx?.send_oct)}
                  </Typography>
                </Box>
              </Grid2>
            </Grid2>

            {(emqx?.inflight_cnt > 0 ||
              emqx?.mqueue_len > 0 ||
              emqx?.mqueue_dropped > 0) && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  Queue Health
                </Typography>
                <InfoRow
                  label="Inflight"
                  value={String(emqx?.inflight_cnt || 0)}
                />
                <InfoRow label="Queued" value={String(emqx?.mqueue_len || 0)} />
                {emqx?.mqueue_dropped > 0 && (
                  <InfoRow
                    label="Dropped"
                    value={
                      <Chip
                        label={String(emqx.mqueue_dropped)}
                        color="error"
                        size="small"
                        variant="outlined"
                      />
                    }
                  />
                )}
              </>
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ConnectionStatusCard;
